import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  MapPin, Building2, CalendarClock, FileCheck2, CheckCircle2, GraduationCap,
  Stethoscope, Briefcase, TrainFront, TrendingUp,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { useSEO, graph, breadcrumbJsonLd } from "@/lib/seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LeadForm from "@/components/common/LeadForm";
import BrochureGate from "@/components/common/BrochureGate";
import { Breadcrumbs, ErrorState, SectionLabel } from "@/components/common/states";
import { priceRange, STATUS_LABEL } from "@/lib/constants";
import { track, Events } from "@/lib/analytics";
import { trackEvent } from "@/lib/tracking";

const LISTS = [
  { key: "nearby_schools", icon: GraduationCap, title: "Schools" },
  { key: "nearby_hospitals", icon: Stethoscope, title: "Healthcare" },
  { key: "it_parks", icon: Briefcase, title: "IT Parks" },
  { key: "transport", icon: TrainFront, title: "Connectivity & Transport" },
];

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(0);
  const [imgFail, setImgFail] = useState(false);

  useEffect(() => {
    setP(null); setError(false); setActive(0);
    apiGet(`/projects/${slug}`).then((d) => { setP(d); setImgFail(false); track(Events.PROJECT_VIEW, { slug }); trackEvent("view_project", { project_slug: slug, project_name: d.name, location: d.location_name, region: d.region, page_type: "project" }); }).catch(() => setError(true));
  }, [slug]);

  useSEO(p ? {
    title: `${p.name}, ${p.location_name} | ${p.developer_name}`,
    description: `${p.name} by ${p.developer_name} in ${p.location_name}, Pune. ${p.configurations?.length ? p.configurations.join(", ") + ". " : ""}${priceRange(p)}. ${p.rera_number ? "RERA: " + p.rera_number + ". " : ""}Get price, floor plans, RERA and possession details.`,
    path: `/projects/${p.slug}`, image: p.gallery?.[0],
    jsonLd: graph(
      {
        "@type": "ApartmentComplex", name: p.name,
        ...(p.gallery?.[0] ? { image: p.gallery[0] } : {}),
        ...(p.description ? { description: p.description } : {}),
        address: { "@type": "PostalAddress", addressLocality: p.location_name, addressRegion: "Maharashtra", addressCountry: "IN" },
      },
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Projects", path: "/projects" }, { name: p.name }]),
    ),
  } : { title: "Project" });

  if (error) return <div className="ec-container py-20"><ErrorState /></div>;
  if (!p) return <div className="ec-container py-20 text-center text-muted-foreground">Loading project…</div>;

  const images = p.gallery && p.gallery.length ? p.gallery : (p.primary_image ? [p.primary_image] : []);
  const hasImages = images.length > 0;
  const imgLabel = p.image_source_type === "ACTUAL_PHOTO" ? "Actual Image"
    : p.image_source_type === "ARTISTIC_IMPRESSION" ? "Artist's Impression" : "";

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[52vh] min-h-[380px] bg-primary">
        {hasImages && !imgFail ? (
          <img src={images[active]} alt={p.name} referrerPolicy="no-referrer" onError={() => setImgFail(true)} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-primary to-[#122a22]">
            <p className="text-white/80 text-sm uppercase tracking-widest mb-2">Official image pending approval</p>
            <p className="font-display text-2xl text-white/90">{p.developer_name}</p>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="relative ec-container h-full flex flex-col justify-end pb-8">
          <div className="mb-4"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Projects", to: "/projects" }, { label: p.name }]} /></div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm">{p.region_label}</span>
            {p.status && <span className="bg-white/15 text-white text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm backdrop-blur">{STATUS_LABEL[p.status] || p.status}</span>}
            {imgLabel && <span className="bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-sm">{imgLabel}</span>}
            {p.source_verified && <span className="bg-green-600 text-white text-[10px] px-2 py-0.5 rounded-sm">Verified</span>}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white">{p.name}</h1>
          <p className="flex items-center gap-1.5 text-white/85 mt-2"><MapPin className="h-4 w-4" aria-hidden /> {p.location_name}, Pune</p>
        </div>
      </section>

      {hasImages && images.length > 1 && (
      <div className="ec-container -mt-6 relative z-10">
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
          {images.map((g, i) => (
            <button key={i} onClick={() => setActive(i)} className={`shrink-0 h-16 w-24 rounded-md overflow-hidden border-2 ${active === i ? "border-accent" : "border-white"}`}>
              <img src={g} alt={`${p.name} ${i + 1}`} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      )}

      <div className="ec-container mt-10 grid lg:grid-cols-[1fr_380px] gap-12">
        <div className="min-w-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 p-6 rounded-lg border border-border bg-white">
            <Stat icon={Building2} label="Developer" value={p.developer_name} />
            <Stat icon={CalendarClock} label="Possession" value={p.possession} />
            <Stat icon={FileCheck2} label="RERA" value={p.rera_number} />
            <Stat icon={TrendingUp} label="Price Range" value={priceRange(p)} />
          </div>

          <Section title="Project Overview">
            <p className="text-muted-foreground leading-relaxed">{p.description}</p>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MetaBox label="Configurations" value={p.configurations?.join(", ")} />
              <MetaBox label="Area Range" value={p.area_range} />
              <MetaBox label="Total Units" value={p.total_units} />
              <MetaBox label="Project Area" value={p.project_area} />
            </div>
          </Section>

          {p.amenities?.length > 0 && (
            <Section title="Amenities">
              <div className="flex flex-wrap gap-2">
                {p.amenities.map((a) => <span key={a} className="px-3 py-1.5 rounded-sm bg-muted text-sm text-foreground/80">{a}</span>)}
              </div>
            </Section>
          )}

          {p.master_plan && (
            <Section title="Master Plan">
              <img src={p.master_plan} alt={`${p.name} master plan`} className="w-full rounded-md border border-border object-cover max-h-96" loading="lazy" />
            </Section>
          )}

          {p.floor_plans?.length > 0 && (
            <Section title="Floor Plans">
              <div className="grid sm:grid-cols-2 gap-4">
                {p.floor_plans.map((f, i) => (
                  <div key={i} className="rounded-md border border-border overflow-hidden bg-white">
                    <img src={f.image} alt={`${f.title} plan`} className="h-40 w-full object-cover" loading="lazy" />
                    <div className="p-4 flex items-center justify-between">
                      <div><p className="font-medium text-foreground">{f.title}</p><p className="text-xs text-muted-foreground">{f.area}</p></div>
                      <span className="text-sm text-primary font-medium">{f.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Location & Connectivity">
            <p className="text-muted-foreground leading-relaxed mb-5">{p.connectivity}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {LISTS.map((l) => (p[l.key]?.length > 0) && (
                <div key={l.key} className="p-5 rounded-md border border-border bg-white">
                  <h4 className="flex items-center gap-2 font-medium text-foreground mb-3"><l.icon className="h-4 w-4 text-primary" aria-hidden /> {l.title}</h4>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    {p[l.key].map((x, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent mt-0.5 shrink-0" aria-hidden /> {x}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          {/* Map placeholder */}
          <Section title="Location Map">
            <div className="rounded-md border border-border bg-muted h-56 flex flex-col items-center justify-center text-center px-6">
              <MapPin className="h-8 w-8 text-primary mb-3" aria-hidden />
              <p className="text-sm text-muted-foreground">Interactive map for {p.location_name}, Pune will appear here once a map provider is configured.</p>
            </div>
          </Section>

          {p.investment_notes && (
            <Section title="Investment Considerations">
              <p className="text-muted-foreground leading-relaxed">{p.investment_notes}</p>
            </Section>
          )}

          {p.faqs?.length > 0 && (
            <Section title="FAQs">
              <Accordion type="single" collapsible>
                {p.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}>
                    <AccordionTrigger className="text-left text-foreground">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Section>
          )}
        </div>

        <aside>
          <div className="lg:sticky lg:top-28 rounded-lg border border-border bg-white p-6">
            <SectionLabel>Enquire</SectionLabel>
            <h3 className="font-display text-xl text-foreground mb-4">Get Project Details</h3>
            <LeadForm source="project" variant="contact" compact submitLabel="Request Details"
              defaults={{ project_slug: p.slug, project_name: p.name, property_name: p.name, location: p.location_name, region: p.region }} />
            {p.official_source_url && (
              <a href={p.official_source_url} target="_blank" rel="noreferrer" className="mt-3 block text-center text-xs text-muted-foreground underline underline-offset-4 hover:text-primary">
                View official developer source
              </a>
            )}
            <BrochureGate project={p} />
          </div>
        </aside>
      </div>
      <div className="h-16" />
    </div>
  );
}

function Section({ title, children }) {
  return <section className="mt-10"><h2 className="font-display text-2xl text-foreground mb-4">{title}</h2>{children}</section>;
}
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-sm bg-primary/5 text-primary grid place-items-center shrink-0"><Icon className="h-4 w-4" aria-hidden /></div>
      <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p><p className="text-sm font-medium text-foreground">{value}</p></div>
    </div>
  );
}
function MetaBox({ label, value }) {
  return <div className="p-4 rounded-md bg-muted"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p><p className="text-sm font-medium text-foreground mt-0.5">{value}</p></div>;
}
